/**
 * Reflection Service
 * Centralizes all API calls and business logic for reflection operations
 */

import { Database, supabase } from "@/lib/supabase";
import { aiService } from "@/lib/ai-service";

export interface ReflectionData {
	title: string;
	description: string;
	topic_tags?: string[];
	theme_id?: string;
	is_draft?: boolean;
	assigned_topic_id?: string;
	current_state?: string;
	future_state?: string;
	qa_results?: {
		history: Array<{ question: string; answer: string }>;
		currentQuestion?: string;
		currentAnswer?: string;
		conversationHistory?: Array<{ role: string; content: string }>;
		selectedCategory?: string | null;
		selectedTheme?: any;
		currentStep?: string;
		aiGeneratedFactors?: any[];
		aiGeneratedSignals?: any[];
		aiGeneratedActions?: any[];
	};
}

export interface ContributingFactor {
	id: string;
	title: string;
	description: string;
	selected: boolean;
}

export interface Signal {
	id: string;
	title: string;
	description: string;
	selected: boolean;
}

export interface CorrectiveAction {
	id: string;
	title: string;
	description: string;
	selected: boolean;
}

export interface ActionLink {
	correctiveActionId: string;
	linkedFactors: string[];
	linkedSignals: string[];
}

/**
 * Save or create a reflection
 * Handles the full reflection data structure including qa_results, current_state, future_state
 */
export async function saveReflection(
	reflectionId: string | null,
	data: ReflectionData,
	userId: string,
): Promise<{ id: string; error: null } | { id: null; error: Error }> {
	try {
		// Build the update/insert payload
		const payload: Database["public"]["Tables"]["reflections"]["Update"] = {
			title: data.title || "Untitled Reflection",
			description: data.description || "",
			topic_tags: data.topic_tags || [],
			is_draft: data.is_draft ?? true,
			updated_at: new Date().toISOString(),
		};

		// Add optional fields if they exist
		if (data.current_state !== undefined) {
			payload.current_state = data.current_state;
		}
		if (data.future_state !== undefined) {
			payload.future_state = data.future_state;
		}
		if (data.qa_results !== undefined) {
			payload.qa_results = data.qa_results;
		}

		if (reflectionId) {
			// Update existing reflection
			const { data: updated, error } = await supabase
				.from("reflections")
				.update(payload)
				.eq("id", reflectionId)
				.eq("user_id", userId)
				.select()
				.single();

			if (error) throw error;
			return { id: updated.id, error: null };
		} else {
			// Create new reflection
			const { data: created, error } = await supabase
				.from("reflections")
				.insert({
					user_id: userId,
					...payload,
				})
				.select()
				.single();

			if (error) throw error;
			return { id: created.id, error: null };
		}
	} catch (error) {
		console.error("Error saving reflection:", error);
		return {
			id: null,
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}
}

/**
 * Load a reflection by ID
 */
export async function loadReflection(
	reflectionId: string,
	userId: string,
): Promise<{ data: any; error: null } | { data: null; error: Error }> {
	try {
		const { data, error } = await supabase
			.from("reflections")
			.select("*")
			.eq("id", reflectionId)
			.eq("user_id", userId)
			.single();

		if (error) throw error;
		return { data, error: null };
	} catch (error) {
		console.error("Error loading reflection:", error);
		return {
			data: null,
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}
}

/**
 * Complete a reflection (mark as not draft)
 */
export async function completeReflection(
	reflectionId: string,
	userId: string,
	data: {
		contributing_factors: ContributingFactor[];
		signals: Signal[];
		future_state?: string;
		corrective_actions: CorrectiveAction[];
		action_links: ActionLink[];
	},
): Promise<{ success: boolean; error: Error | null }> {
	try {
		// Update reflection
		const { error: updateError } = await supabase
			.from("reflections")
			.update({
				is_draft: false,
				updated_at: new Date().toISOString(),
			})
			.eq("id", reflectionId)
			.eq("user_id", userId);

		if (updateError) throw updateError;

		// Save contributing factors
		if (data.contributing_factors.length > 0) {
			const factorsToInsert = data.contributing_factors
				.filter((f) => f.selected)
				.map((f) => ({
					reflection_id: reflectionId,
					title: f.title,
					description: f.description,
				}));

			if (factorsToInsert.length > 0) {
				const { error: factorsError } = await supabase
					.from("contributing_factors")
					.upsert(factorsToInsert, { onConflict: "reflection_id,title" });

				if (factorsError) throw factorsError;
			}
		}

		// Save signals
		if (data.signals.length > 0) {
			const signalsToInsert = data.signals
				.filter((s) => s.selected)
				.map((s) => ({
					reflection_id: reflectionId,
					title: s.title,
					description: s.description,
				}));

			if (signalsToInsert.length > 0) {
				const { error: signalsError } = await supabase
					.from("signals")
					.upsert(signalsToInsert, { onConflict: "reflection_id,title" });

				if (signalsError) throw signalsError;
			}
		}

		// Save future state
		if (data.future_state) {
			const { error: futureStateError } = await supabase
				.from("reflections")
				.update({ future_state: data.future_state })
				.eq("id", reflectionId);

			if (futureStateError) throw futureStateError;
		}

		// Save corrective actions and links
		if (data.corrective_actions.length > 0) {
			const actionsToInsert = data.corrective_actions
				.filter((a) => a.selected)
				.map((a) => ({
					reflection_id: reflectionId,
					title: a.title,
					description: a.description,
				}));

			if (actionsToInsert.length > 0) {
				const { error: actionsError } = await supabase
					.from("corrective_actions")
					.insert(actionsToInsert)
					.select();

				if (actionsError) throw actionsError;
			}
		}

		return { success: true, error: null };
	} catch (error) {
		console.error("Error completing reflection:", error);
		return {
			success: false,
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}
}

/**
 * Generate contributing factors using AI
 */
export async function generateContributingFactors(
	description: string,
): Promise<{ factors: ContributingFactor[]; error: Error | null }> {
	try {
		const response = await aiService.generateContributingFactors(description);
		const factors: ContributingFactor[] = (response.factors || []).map(
			(f: any, index: number) => ({
				id: `factor-${Date.now()}-${index}`,
				title: f.title || "",
				description: f.description || "",
				selected: false,
			}),
		);

		return { factors, error: null };
	} catch (error) {
		console.error("Error generating contributing factors:", error);
		return {
			factors: [],
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}
}

/**
 * Generate signals using AI
 */
export async function generateSignals(
	description: string,
	contributingFactors: ContributingFactor[],
): Promise<{ signals: Signal[]; error: Error | null }> {
	try {
		const response = await aiService.generateSignals(
			description,
			contributingFactors,
		);
		const signals: Signal[] = (response.signals || []).map(
			(s: any, index: number) => ({
				id: `signal-${Date.now()}-${index}`,
				title: s.title || "",
				description: s.description || "",
				selected: false,
			}),
		);

		return { signals, error: null };
	} catch (error) {
		console.error("Error generating signals:", error);
		return {
			signals: [],
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}
}

/**
 * Generate corrective actions using AI
 */
export async function generateCorrectiveActions(
	description: string,
	contributingFactors: ContributingFactor[],
	signals: Signal[],
): Promise<{ actions: CorrectiveAction[]; error: Error | null }> {
	try {
		const response = await aiService.generateCorrectiveActions(
			description,
			contributingFactors,
			signals,
		);
		const actions: CorrectiveAction[] = (response.actions || []).map(
			(a: any, index: number) => ({
				id: `action-${Date.now()}-${index}`,
				title: a.title || "",
				description: a.description || "",
				selected: false,
			}),
		);

		return { actions, error: null };
	} catch (error) {
		console.error("Error generating corrective actions:", error);
		return {
			actions: [],
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}
}

/**
 * Generate future state using AI
 */
export async function generateFutureState(
	description: string,
): Promise<{ futureState: string; error: Error | null }> {
	try {
		const response = await aiService.generateFutureState(description);
		return { futureState: response.futureState || "", error: null };
	} catch (error) {
		console.error("Error generating future state:", error);
		return {
			futureState: "",
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}
}

/**
 * Save contributing factors for a reflection
 */
export async function saveContributingFactors(
	reflectionId: string,
	factors: ContributingFactor[],
	userId: string,
): Promise<{ success: boolean; error: Error | null }> {
	try {
		// Check if reflection already has contributing factors
		const { data: existingFactors, error: checkError } = await supabase
			.from("contributing_factors")
			.select("id")
			.eq("reflection_id", reflectionId)
			.limit(1);

		if (checkError) throw checkError;

		// Only proceed if no existing factors
		if (existingFactors && existingFactors.length > 0) {
			return { success: true, error: null };
		}

		// Insert new factors (only selected ones)
		const factorsToSave = factors
			.filter((factor) => factor.selected)
			.map((factor) => ({
				reflection_id: reflectionId,
				title: factor.title,
				description: factor.description,
			}));

		if (factorsToSave.length > 0) {
			const { error } = await supabase
				.from("contributing_factors")
				.insert(factorsToSave);

			if (error) throw error;
		}

		return { success: true, error: null };
	} catch (error) {
		console.error("Error saving contributing factors:", error);
		return {
			success: false,
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}
}

/**
 * Save signals for a reflection
 */
export async function saveSignals(
	reflectionId: string,
	signals: Signal[],
	userId: string,
): Promise<{ success: boolean; error: Error | null }> {
	try {
		// Check if reflection already has signals
		const { data: existingSignals, error: checkError } = await supabase
			.from("signals")
			.select("id")
			.eq("reflection_id", reflectionId)
			.limit(1);

		if (checkError) throw checkError;

		// Only proceed if no existing signals
		if (existingSignals && existingSignals.length > 0) {
			return { success: true, error: null };
		}

		// Insert new signals (only selected ones)
		const signalsToSave = signals
			.filter((signal) => signal.selected)
			.map((signal) => ({
				reflection_id: reflectionId,
				title: signal.title,
				description: signal.description,
			}));

		if (signalsToSave.length > 0) {
			const { error } = await supabase.from("signals").insert(signalsToSave);

			if (error) throw error;
		}

		return { success: true, error: null };
	} catch (error) {
		console.error("Error saving signals:", error);
		return {
			success: false,
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}
}

/**
 * Save corrective actions for a reflection
 */
export async function saveCorrectiveActions(
	reflectionId: string,
	actions: CorrectiveAction[],
	userId: string,
): Promise<{ success: boolean; error: Error | null }> {
	try {
		// Check if reflection already has corrective actions
		const { data: existingActions, error: checkError } = await supabase
			.from("corrective_actions")
			.select("id")
			.eq("reflection_id", reflectionId)
			.limit(1);

		if (checkError) throw checkError;

		// Only proceed if no existing actions
		if (existingActions && existingActions.length > 0) {
			return { success: true, error: null };
		}

		// Insert new actions (only selected ones)
		const actionsToSave = actions
			.filter((action) => action.selected)
			.map((action) => ({
				reflection_id: reflectionId,
				title: action.title,
				description: action.description,
				priority: 1,
				status: "not_started",
			}));

		if (actionsToSave.length > 0) {
			const { error } = await supabase
				.from("corrective_actions")
				.insert(actionsToSave);

			if (error) throw error;
		}

		return { success: true, error: null };
	} catch (error) {
		console.error("Error saving corrective actions:", error);
		return {
			success: false,
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}
}

/**
 * Save action links for a specific corrective action
 * Performs smart updates: only adds new links and removes deselected ones
 */
export async function saveActionLinks(
	correctiveActionId: string,
	linkedFactorIds: string[],
	linkedSignalIds: string[],
	userId: string,
): Promise<{ success: boolean; error: Error | null }> {
	try {
		// Load existing factor links
		const { data: existingFactorLinks, error: existingFactorError } =
			await supabase
				.from("corrective_action_contributing_factor_links")
				.select("id, contributing_factor_id")
				.eq("corrective_action_id", correctiveActionId);

		if (existingFactorError) throw existingFactorError;

		const existingFactorIds = (existingFactorLinks || []).map(
			(l) => l.contributing_factor_id,
		);

		// Determine what to delete and what to insert for factors
		const factorsToDelete = (existingFactorLinks || [])
			.filter((link) => !linkedFactorIds.includes(link.contributing_factor_id))
			.map((link) => link.id);

		const factorsToInsert = linkedFactorIds
			.filter((factorId) => !existingFactorIds.includes(factorId))
			.map((factorId) => ({
				corrective_action_id: correctiveActionId,
				contributing_factor_id: factorId,
				user_id: userId,
			}));

		// Load existing signal links
		const { data: existingSignalLinks, error: existingSignalError } =
			await supabase
				.from("corrective_action_signal_links")
				.select("id, signal_id")
				.eq("corrective_action_id", correctiveActionId);

		if (existingSignalError) throw existingSignalError;

		const existingSignalIds = (existingSignalLinks || []).map(
			(l) => l.signal_id,
		);

		// Determine what to delete and what to insert for signals
		const signalsToDelete = (existingSignalLinks || [])
			.filter((link) => !linkedSignalIds.includes(link.signal_id))
			.map((link) => link.id);

		const signalsToInsert = linkedSignalIds
			.filter((signalId) => !existingSignalIds.includes(signalId))
			.map((signalId) => ({
				corrective_action_id: correctiveActionId,
				signal_id: signalId,
				user_id: userId,
			}));

		// Perform deletions for factors
		if (factorsToDelete.length > 0) {
			const { error: deleteFactorError } = await supabase
				.from("corrective_action_contributing_factor_links")
				.delete()
				.in("id", factorsToDelete);

			if (deleteFactorError) throw deleteFactorError;
		}

		// Perform insertions for factors
		if (factorsToInsert.length > 0) {
			const { error: insertFactorError } = await supabase
				.from("corrective_action_contributing_factor_links")
				.insert(factorsToInsert);

			if (insertFactorError) throw insertFactorError;
		}

		// Perform deletions for signals
		if (signalsToDelete.length > 0) {
			const { error: deleteSignalError } = await supabase
				.from("corrective_action_signal_links")
				.delete()
				.in("id", signalsToDelete);

			if (deleteSignalError) throw deleteSignalError;
		}

		// Perform insertions for signals
		if (signalsToInsert.length > 0) {
			const { error: insertSignalError } = await supabase
				.from("corrective_action_signal_links")
				.insert(signalsToInsert);

			if (insertSignalError) throw insertSignalError;
		}

		return { success: true, error: null };
	} catch (error) {
		console.error("Error saving action links:", error);
		return {
			success: false,
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}
}

/**
 * Load action links for corrective actions
 * Returns links in the format expected by the ActionLink interface
 */
export async function loadActionLinks(
	reflectionId: string,
	userId: string,
): Promise<{ links: ActionLink[]; error: Error | null }> {
	try {
		// Verify user owns the reflection
		const { data: reflection, error: reflectionError } = await supabase
			.from("reflections")
			.select("user_id")
			.eq("id", reflectionId)
			.eq("user_id", userId)
			.maybeSingle();

		if (reflectionError || !reflection) {
			return {
				links: [],
				error: new Error("Reflection not found or access denied"),
			};
		}

		// Get all corrective actions for this reflection
		const { data: actions, error: actionsError } = await supabase
			.from("corrective_actions")
			.select("id")
			.eq("reflection_id", reflectionId);

		if (actionsError) throw actionsError;

		if (!actions || actions.length === 0) {
			return { links: [], error: null };
		}

		const actionIds = actions.map((a) => a.id);

		// Load factor links
		const { data: factorLinks, error: factorLinksError } = await supabase
			.from("corrective_action_contributing_factor_links")
			.select("corrective_action_id, contributing_factor_id")
			.in("corrective_action_id", actionIds);

		if (factorLinksError) throw factorLinksError;

		// Load signal links
		const { data: signalLinks, error: signalLinksError } = await supabase
			.from("corrective_action_signal_links")
			.select("corrective_action_id, signal_id")
			.in("corrective_action_id", actionIds);

		if (signalLinksError) throw signalLinksError;

		// Group links by corrective action
		const linksMap = new Map<string, ActionLink>();

		actionIds.forEach((actionId) => {
			linksMap.set(actionId, {
				correctiveActionId: actionId,
				linkedFactors: [],
				linkedSignals: [],
			});
		});

		// Add factor links
		(factorLinks || []).forEach((link) => {
			const actionLink = linksMap.get(link.corrective_action_id);
			if (actionLink) {
				actionLink.linkedFactors.push(link.contributing_factor_id);
			}
		});

		// Add signal links
		(signalLinks || []).forEach((link) => {
			const actionLink = linksMap.get(link.corrective_action_id);
			if (actionLink) {
				actionLink.linkedSignals.push(link.signal_id);
			}
		});

		const links = Array.from(linksMap.values());
		return { links, error: null };
	} catch (error) {
		console.error("Error loading action links:", error);
		return {
			links: [],
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}
}
